import { useContext, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Typography from "@mui/material/Typography";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CachedIcon from "@mui/icons-material/Cached";
import TextField from "../Form/TextField";
import Button from "../Buttons/Button";
import IconButton from "../Buttons/IconButton";
import ModalBasicLayout from "./ModalBasicLayout";
import ModalMedia from "./ModalMedia";
import ContainerModalActions from "../Containers/ContainerModalActions";
import Loader from "../Loaders/Loader";
import { ConfirmAlert, SimpleAlert } from "../Alerts/Alerts";
import { UploadFilesContext } from "../../contexts/UploadFilesContext";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import { getPathDatabaseByRole } from "../../actions/auth";
import { canWriteCreativeTemplates } from "../../features/creativeTemplates/utils/permissions";
import {
  startDeleteAnimateImageOutro,
  startListAnimateImageOutros,
  startSaveAnimateImageOutro,
  startSetSelectedAnimateImageOutro,
} from "../../actions/animateImageLibrary";
import {
  ANIMATE_IMAGE_DELETE_OUTRO,
  ANIMATE_IMAGE_DELETE_OUTRO_ERROR,
  ANIMATE_IMAGE_IMPORT_OUTRO,
  ANIMATE_IMAGE_IMPORT_OUTRO_EMPTY,
  ANIMATE_IMAGE_IMPORT_OUTRO_ERROR,
  ANIMATE_IMAGE_IMPORT_OUTRO_TITLE,
  ANIMATE_IMAGE_OUTRO_CLEAR,
  ANIMATE_IMAGE_OUTRO_HELPER,
  ANIMATE_IMAGE_OUTRO_INVALID_TYPE,
  ANIMATE_IMAGE_OUTRO_LABEL,
  ANIMATE_IMAGE_SAVE_OUTRO,
  ANIMATE_IMAGE_SAVE_OUTRO_ERROR,
  ANIMATE_IMAGE_SAVE_OUTRO_SUCCESS,
  ANIMATE_IMAGE_SAVE_OUTRO_TITLE,
  ANIMATE_IMAGE_USE_SELECTED,
  BUTTON_CANCEL,
  BUTTON_REPLACE,
  BUTTON_SELECT,
  BUTTON_DELETE,
  BUTTON_GO_BACK,
  BUTTON_SAVE,
  NAME,
} from "../../i18n/keysTranslations";
import { BORDER_RADIUS } from "../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import {
  ANIMATE_IMAGE_OUTRO_ACCEPT,
  ANIMATE_IMAGE_OUTRO_NAME_MAX_LENGTH,
} from "../../utils/animateImageConstants";

const optionalRules = { required: false };
const OUTRO_MIME_TYPES = new Set([
  "video/mp4",
  "video/quicktime",
  "video/webm",
]);
const OUTRO_EXTENSIONS = new Set(["mp4", "mov", "webm"]);

const isAcceptedOutroFile = (file) => {
  const mime = String(file?.type || "").toLowerCase();
  const extension = String(file?.name || "")
    .split(".")
    .pop()
    .toLowerCase();
  return OUTRO_MIME_TYPES.has(mime) || OUTRO_EXTENSIONS.has(extension);
};

const AnimateImageOutroField = ({
  disabled = false,
  value = null,
  onChange,
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { uploadFile } = useContext(UploadFilesContext) || {};
  const { role, userID: advertiserID } = dispatch(getPathDatabaseByRole());
  const canWrite = canWriteCreativeTemplates({ role });
  const importRequestRef = useRef(0);
  const fileInputRef = useRef(null);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [savedOutros, setSavedOutros] = useState([]);
  const [isLoadingOutros, setIsLoadingOutros] = useState(false);
  const [outrosLoadFailed, setOutrosLoadFailed] = useState(false);
  const [deletingID, setDeletingID] = useState("");
  const [selectedOutroID, setSelectedOutroID] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [videoSizes, setVideoSizes] = useState({});

  const {
    control: saveControl,
    handleSubmit,
    reset: resetSave,
    watch,
    formState: { errors: saveErrors },
  } = useForm({
    defaultValues: {
      name: "",
    },
  });
  const saveName = watch("name");
  const canSave =
    Boolean(String(saveName || "").trim()) && Boolean(value?.videoUrl);

  const openPreview = (event, videoUrl) => {
    event.stopPropagation();
    if (!videoUrl) return;
    setPreviewUrl(videoUrl);
  };

  const onPreviewMetadata = (outroID) => (event) => {
    const width = event.currentTarget.videoWidth;
    const height = event.currentTarget.videoHeight;
    if (!width || !height) return;
    setVideoSizes((current) => {
      const known = current[outroID];
      if (known?.width === width && known?.height === height) return current;
      return { ...current, [outroID]: { width, height } };
    });
  };

  const persistSelection = async (outro) => {
    onChange(outro);
    const result = await dispatch(
      startSetSelectedAnimateImageOutro({ outroID: outro?.id || "" })
    );
    if (!result?.ok && result?.reason !== "unauthorized") {
      SimpleAlert({
        title: t(ANIMATE_IMAGE_SAVE_OUTRO_ERROR),
        icon: "error",
      });
    }
  };

  const openSave = () => {
    if (!value?.videoUrl) return;
    resetSave({ name: value?.id ? "" : value.name || "" });
    setIsSaveOpen(true);
  };

  const onFileChange = async (event) => {
    const nextFile = event.target.files?.[0];
    event.target.value = "";
    if (!nextFile || isUploading) return;

    const rejectFile = (message) => {
      SimpleAlert({ title: message, icon: "error" });
    };

    if (!isAcceptedOutroFile(nextFile)) {
      rejectFile(t(ANIMATE_IMAGE_OUTRO_INVALID_TYPE));
      return;
    }

    if (!advertiserID || typeof uploadFile !== "function") {
      rejectFile(t(ANIMATE_IMAGE_SAVE_OUTRO_ERROR));
      return;
    }

    setIsUploading(true);
    const videoUrl = await uploadFile({
      file: nextFile,
      pathStorage: `animate-image-outros/${advertiserID}/`,
      doCompression: false,
      disableVerificationMedia: true,
    });
    setIsUploading(false);
    if (!videoUrl) {
      rejectFile(t(ANIMATE_IMAGE_SAVE_OUTRO_ERROR));
      return;
    }

    const fileName = String(nextFile.name || "").replace(/\.[^.]+$/, "");
    onChange({
      id: "",
      name: fileName,
      videoUrl,
    });
  };

  const onSave = async ({ name }) => {
    if (!value?.videoUrl || isSaving) {
      SimpleAlert({
        title: t(ANIMATE_IMAGE_SAVE_OUTRO_ERROR),
        icon: "error",
      });
      return;
    }

    setIsSaving(true);
    const result = await dispatch(
      startSaveAnimateImageOutro({ name, videoUrl: value.videoUrl })
    );
    setIsSaving(false);
    if (!result?.ok) {
      SimpleAlert({
        title: t(ANIMATE_IMAGE_SAVE_OUTRO_ERROR),
        icon: "error",
      });
      return;
    }

    onChange(result.outro);
    setIsSaveOpen(false);
    SimpleAlert({
      title: t(ANIMATE_IMAGE_SAVE_OUTRO_SUCCESS),
      icon: "success",
    });
  };

  const openImport = async () => {
    const requestID = importRequestRef.current + 1;
    importRequestRef.current = requestID;
    setIsImportOpen(true);
    setIsLoadingOutros(true);
    setOutrosLoadFailed(false);
    setSelectedOutroID(value?.id || "");
    setSavedOutros([]);
    const result = await dispatch(startListAnimateImageOutros());
    if (importRequestRef.current !== requestID) return;
    setIsLoadingOutros(false);
    if (!result?.ok) {
      setOutrosLoadFailed(true);
      SimpleAlert({
        title: t(ANIMATE_IMAGE_IMPORT_OUTRO_ERROR),
        icon: "error",
      });
      return;
    }
    setSavedOutros(result.outros || []);
  };

  const onUseSelected = async () => {
    const item = savedOutros.find((entry) => entry.id === selectedOutroID);
    if (!item) return;
    setIsImportOpen(false);
    await persistSelection(item);
  };

  const onClear = async () => {
    await persistSelection(null);
  };

  const onDelete = async (event, outroID) => {
    event.stopPropagation();
    if (deletingID) return;
    const confirmed = await ConfirmAlert({
      title: t(ANIMATE_IMAGE_DELETE_OUTRO),
      confirmButtonText: t(BUTTON_DELETE),
    });
    if (!confirmed?.isConfirmed) return;
    setDeletingID(outroID);
    const result = await dispatch(startDeleteAnimateImageOutro({ outroID }));
    setDeletingID("");
    if (!result?.ok) {
      SimpleAlert({
        title: t(ANIMATE_IMAGE_DELETE_OUTRO_ERROR),
        icon: "error",
      });
      return;
    }
    setSavedOutros((current) => current.filter((item) => item.id !== outroID));
    setSelectedOutroID((current) => (current === outroID ? "" : current));
    if (value?.id === outroID || result.clearedSelection) {
      onChange(null);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, color: "text.primary" }}
          >
            {t(ANIMATE_IMAGE_OUTRO_LABEL)}
          </Typography>
          <Box sx={{ display: "flex", flexShrink: 0, alignItems: "center" }}>
            <IconButton
              tooltipText={ANIMATE_IMAGE_SAVE_OUTRO}
              disabled={
                disabled || !canWrite || !value?.videoUrl || isUploading
              }
              edge={false}
              onClick={openSave}
              sx={{ p: "2px", width: 28, height: 28 }}
            >
              <SaveOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton
              tooltipText={ANIMATE_IMAGE_IMPORT_OUTRO}
              disabled={disabled}
              edge={false}
              onClick={openImport}
              sx={{ p: "2px", width: 28, height: 28 }}
            >
              <CollectionsBookmarkOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {value?.videoUrl ? null : (
          <Button
            color="secondary"
            disabled={disabled || isUploading}
            loading={isUploading}
            onClick={() => fileInputRef.current?.click()}
            sx={{ alignSelf: "flex-start" }}
          >
            {t(BUTTON_SELECT)}
          </Button>
        )}
        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept={ANIMATE_IMAGE_OUTRO_ACCEPT}
          disabled={disabled || isUploading}
          onChange={onFileChange}
        />

        {value?.videoUrl ? (
          <Box
            sx={{
              p: 1.5,
              border: `1px solid ${INPUT_COLOR_BORDER}`,
              borderRadius: BORDER_RADIUS,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Box
              key={value.videoUrl}
              component="video"
              src={value.videoUrl}
              autoPlay
              muted
              loop
              playsInline
              onClick={(event) => openPreview(event, value.videoUrl)}
              sx={{
                width: 96,
                height: 96,
                objectFit: "cover",
                borderRadius: 1,
                bgcolor: "grey.100",
                flexShrink: 0,
                cursor: "pointer",
              }}
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 700 }} noWrap>
                {value.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(ANIMATE_IMAGE_OUTRO_HELPER)}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexShrink: 0, alignItems: "center" }}>
              <IconButton
                tooltipText={BUTTON_REPLACE}
                disabled={disabled || isUploading}
                loading={isUploading}
                edge={false}
                onClick={() => fileInputRef.current?.click()}
                sx={{ p: 0.5 }}
              >
                <CachedIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                tooltipText={ANIMATE_IMAGE_OUTRO_CLEAR}
                disabled={disabled}
                edge={false}
                onClick={onClear}
                sx={{ p: 0.5 }}
              >
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        ) : null}
      </Box>

      <ModalBasicLayout
        modalOpen={isSaveOpen}
        onCloseModal={isSaving ? () => {} : () => setIsSaveOpen(false)}
        title={t(ANIMATE_IMAGE_SAVE_OUTRO_TITLE)}
        ariaLabel={t(ANIMATE_IMAGE_SAVE_OUTRO_TITLE)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            name="name"
            control={saveControl}
            errors={saveErrors}
            label={t(NAME)}
            variant="filled"
            fullWidth
            autoFocus
            disabled={isSaving}
            rules={optionalRules}
            inputProps={{ maxLength: ANIMATE_IMAGE_OUTRO_NAME_MAX_LENGTH }}
          />
        </Box>
        <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, mt: 0.5 }}>
          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_CANCEL,
              disabled: isSaving,
              onClick: () => setIsSaveOpen(false),
            }}
            propsPrimaryButton={{
              showButton: true,
              text: BUTTON_SAVE,
              disabled: !canSave || isSaving,
              loading: isSaving,
              onClick: handleSubmit(onSave),
            }}
          />
        </DialogActions>
      </ModalBasicLayout>

      <ModalBasicLayout
        modalOpen={isImportOpen}
        onCloseModal={() => setIsImportOpen(false)}
        title={t(ANIMATE_IMAGE_IMPORT_OUTRO_TITLE)}
        ariaLabel={t(ANIMATE_IMAGE_IMPORT_OUTRO_TITLE)}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        {isLoadingOutros ? (
          <Loader hasMarginTop={false} size={28} fullWidth />
        ) : null}
        {!isLoadingOutros && !outrosLoadFailed && !savedOutros.length ? (
          <Typography variant="body2" color="text.secondary">
            {t(ANIMATE_IMAGE_IMPORT_OUTRO_EMPTY)}
          </Typography>
        ) : null}
        {!isLoadingOutros && savedOutros.length ? (
          <RadioGroup
            value={selectedOutroID}
            onChange={(event) => setSelectedOutroID(event.target.value)}
          >
            {savedOutros.map((item) => (
              <Box
                key={item.id}
                sx={{
                  mb: 1,
                  p: 1.5,
                  border: `1px solid ${INPUT_COLOR_BORDER}`,
                  borderRadius: BORDER_RADIUS,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <FormControlLabel
                  value={item.id}
                  control={<Radio sx={{ p: 0, mr: 1 }} />}
                  label={
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700 }} noWrap>
                        {item.name}
                      </Typography>
                      {videoSizes[item.id] ? (
                        <Typography variant="caption" color="text.secondary">
                          {videoSizes[item.id].width} ×{" "}
                          {videoSizes[item.id].height}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                  sx={{ flex: 1, m: 0, minWidth: 0 }}
                />
                <Box
                  key={item.videoUrl}
                  component="video"
                  src={item.videoUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  onClick={(event) => openPreview(event, item.videoUrl)}
                  onLoadedMetadata={onPreviewMetadata(item.id)}
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 1,
                    bgcolor: "grey.100",
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                />
                {canWrite ? (
                  <IconButton
                    tooltipText={ANIMATE_IMAGE_DELETE_OUTRO}
                    disabled={Boolean(deletingID)}
                    loading={deletingID === item.id}
                    edge={false}
                    onClick={(event) => onDelete(event, item.id)}
                    sx={{ p: 0.5 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                ) : null}
              </Box>
            ))}
          </RadioGroup>
        ) : null}
        <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, mt: 0.5 }}>
          <ContainerModalActions
            hasPadding={false}
            propsSecondaryButton={{
              showButton: true,
              text: BUTTON_GO_BACK,
              onClick: () => setIsImportOpen(false),
            }}
            propsPrimaryButton={{
              showButton: true,
              text: ANIMATE_IMAGE_USE_SELECTED,
              disabled: !selectedOutroID,
              onClick: onUseSelected,
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
      {previewUrl ? (
        <ModalMedia
          url={previewUrl}
          modalOpen
          onCloseModal={(event) => {
            event?.stopPropagation?.();
            setPreviewUrl("");
          }}
          showEditButton={false}
          showDownloadButton={false}
          showShareButton={false}
          showDeleteButton={false}
        />
      ) : null}
    </>
  );
};

export default AnimateImageOutroField;
